import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import type { TMFRanking } from '@/types'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const USER_ID = '00000000-0000-0000-0000-000000000000'

type SortField = 'rank' | 'symbol' | 'cmaTag' | 'estReturn' | 'timesRecommended' | 'previousRank'
type SortDir = 'asc' | 'desc'

// Map Supabase snake_case row to camelCase TMFRanking
function mapRow(row: Record<string, unknown>): TMFRanking {
  return {
    id: row.id as string,
    rank: row.rank as number,
    symbol: row.symbol as string,
    price: row.price as number | null,
    changePercent: row.change_percent as number | null,
    previousRank: row.previous_rank as number | null,
    marketCap: row.market_cap as string | null,
    cmaTag: row.cma_tag as string | null,
    oneYearRevGrowth: row.one_year_rev_growth as string | null,
    estLowReturn: row.est_low_return as number | null,
    estHighReturn: row.est_high_return as number | null,
    timesRecommended: row.times_recommended as number | null,
    comments: row.comments as string | null,
    sourceFilename: row.source_filename as string | null,
    uploadedAt: row.uploaded_at as string,
  }
}

export default function TMFRankingsPage() {
  const [rankings, setRankings] = useState<TMFRanking[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)
  const [showConfirmClear, setShowConfirmClear] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [sortField, setSortField] = useState<SortField>('rank')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchRankings = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('tmf_rankings')
        .select('*')
        .order('rank', { ascending: true })
      if (err) throw new Error(err.message)
      setRankings((data || []).map(mapRow))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rankings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRankings()
  }, [fetchRankings])

  // Clear success/error messages after 5 seconds
  useEffect(() => {
    if (uploadSuccess) {
      const timer = setTimeout(() => setUploadSuccess(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [uploadSuccess])

  useEffect(() => {
    if (uploadError) {
      const timer = setTimeout(() => setUploadError(null), 8000)
      return () => clearTimeout(timer)
    }
  }, [uploadError])

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 5MB.`
    }
    const ext = file.name.toLowerCase().split('.').pop()
    if (ext !== 'csv') {
      return 'Only .csv files are accepted.'
    }
    if (file.type && file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      // Some systems report different MIME types for CSV — also check extension
      if (ext !== 'csv') {
        return 'Only CSV files are accepted.'
      }
    }
    return null
  }

  const parseCSV = (text: string): Record<string, string>[][] => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
    if (lines.length < 2) return []
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    const rows: Record<string, string>[][] = []
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',').map(c => c.trim())
      const row: Record<string, string> = {}
      headers.forEach((h, j) => { row[h] = cols[j] || '' })
      rows.push(row as unknown as Record<string, string>[])
    }
    return rows
  }

  const uploadFile = async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      setUploadError(validationError)
      return
    }

    setUploading(true)
    setUploadError(null)
    setUploadSuccess(null)

    try {
      const text = await file.text()
      const rows = parseCSV(text) as unknown as Record<string, string>[]
      if (rows.length === 0) throw new Error('CSV has no data rows')

      // Delete existing rankings
      const { error: delErr } = await supabase
        .from('tmf_rankings')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000')
      if (delErr) throw new Error(delErr.message)

      // Map CSV rows to DB records
      let skipped = 0
      const dbRows = rows.map((row: Record<string, string>, idx: number) => {
        const rank = parseInt(row['rank'] || String(idx + 1))
        const symbol = (row['symbol'] || row['ticker'] || '').toUpperCase()
        if (!symbol) { skipped++; return null }
        return {
          user_id: USER_ID,
          rank: isNaN(rank) ? idx + 1 : rank,
          symbol,
          price: parseFloat(row['price'] || '') || null,
          change_percent: parseFloat((row['change%'] || row['change_percent'] || row['changepercent'] || '').replace('%', '')) || null,
          previous_rank: parseInt(row['previous rank'] || row['previous_rank'] || row['previousrank'] || '') || null,
          market_cap: row['market cap'] || row['market_cap'] || row['marketcap'] || null,
          cma_tag: row['cma tag'] || row['cma_tag'] || row['cmatag'] || null,
          one_year_rev_growth: row['1-year rev growth'] || row['one_year_rev_growth'] || row['1yearrevgrowth'] || null,
          est_low_return: parseFloat(row['est low return'] || row['est_low_return'] || row['estlowreturn'] || '') || null,
          est_high_return: parseFloat(row['est high return'] || row['est_high_return'] || row['esthighreturn'] || '') || null,
          times_recommended: parseInt(row['times recommended'] || row['times_recommended'] || row['timesrecommended'] || '') || null,
          comments: row['comments'] || null,
          source_filename: file.name,
          uploaded_at: new Date().toISOString(),
        }
      }).filter((r): r is NonNullable<typeof r> => r !== null)

      if (dbRows.length === 0) throw new Error('No valid rows found in CSV')

      const { error: insErr } = await supabase
        .from('tmf_rankings')
        .insert(dbRows)
      if (insErr) throw new Error(insErr.message)

      setUploadSuccess(
        `Uploaded ${dbRows.length} rankings from ${file.name}` +
        (skipped ? ` (${skipped} rows skipped)` : '')
      )
      await fetchRankings()
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) uploadFile(file)
    // Reset input so same file can be re-selected
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) uploadFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleClearRankings = async () => {
    setClearing(true)
    try {
      const { error: delErr, count } = await supabase
        .from('tmf_rankings')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000')
      if (delErr) throw new Error(delErr.message)
      setRankings([])
      setUploadSuccess(`Cleared rankings.`)
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to clear rankings')
    } finally {
      setClearing(false)
      setShowConfirmClear(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  const sortedRankings = [...rankings].sort((a, b) => {
    let cmp = 0
    switch (sortField) {
      case 'rank':
        cmp = a.rank - b.rank
        break
      case 'symbol':
        cmp = a.symbol.localeCompare(b.symbol)
        break
      case 'cmaTag':
        cmp = (a.cmaTag || '').localeCompare(b.cmaTag || '')
        break
      case 'estReturn': {
        const aVal = a.estLowReturn ?? -Infinity
        const bVal = b.estLowReturn ?? -Infinity
        cmp = aVal - bVal
        break
      }
      case 'timesRecommended':
        cmp = (a.timesRecommended ?? 0) - (b.timesRecommended ?? 0)
        break
      case 'previousRank':
        cmp = (a.previousRank ?? Infinity) - (b.previousRank ?? Infinity)
        break
    }
    return sortDir === 'asc' ? cmp : -cmp
  })

  // Metadata
  const sourceFilename = rankings.length > 0 ? rankings[0].sourceFilename : null
  const uploadedAt = rankings.length > 0 ? rankings[0].uploadedAt : null

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => (
    <th
      className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-900 select-none"
      onClick={() => handleSort(field)}
    >
      {label} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400 text-lg">Loading TMF Rankings...</div>
      </div>
    )
  }

  return (
    <div className="p-6 mx-auto" style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          📊 TMF Rankings
        </h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 transition-colors"
          >
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          {rankings.length > 0 && (
            <button
              onClick={() => setShowConfirmClear(true)}
              disabled={clearing}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-red-700 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
            >
              Clear Rankings
            </button>
          )}
        </div>
      </div>

      {/* Success/Error toasts */}
      {uploadSuccess && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
          ✓ {uploadSuccess}
        </div>
      )}
      {uploadError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          ✗ {uploadError}
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Confirmation dialog */}
      {showConfirmClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Clear All Rankings?</h3>
            <p className="text-gray-500 text-sm mb-6">
              Are you sure? This will delete all TMF Rankings data. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmClear(false)}
                className="px-4 py-2 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleClearRankings}
                disabled={clearing}
                className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
              >
                {clearing ? 'Clearing...' : 'Yes, Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content: either rankings table or empty state */}
      {rankings.length === 0 ? (
        /* Empty state with upload zone */
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400'
          }`}
        >
          <div className="text-5xl mb-4">📊</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Rankings Uploaded
          </h2>
          <p className="text-gray-500 mb-4">
            Upload your Motley Fool monthly rankings CSV to get started.
          </p>
          <p className="text-gray-400 text-sm mb-6">
            Drag and drop a .csv file here, or click "Upload CSV" above.
          </p>
          <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 text-left max-w-lg mx-auto">
            <p className="text-gray-500 text-xs font-semibold uppercase mb-2">Expected CSV Columns:</p>
            <p className="text-gray-400 text-xs font-mono leading-relaxed">
              Rank, Symbol, Price, Change%, Previous Rank, Market Cap, CMA Tag,
              1-Year Rev Growth, Est Low Return, Est High Return, Times Recommended, Comments
            </p>
          </div>
          {uploading && (
            <div className="mt-6 flex items-center justify-center gap-2 text-blue-600">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              Uploading...
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Metadata bar */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-4 text-sm text-gray-500">
            {sourceFilename && (
              <span>
                Source: <span className="text-gray-900 font-medium">{sourceFilename}</span>
              </span>
            )}
            {uploadedAt && (
              <span>
                Uploaded: <span className="text-gray-900 font-medium">
                  {new Date(uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </span>
            )}
            <span>
              <span className="text-gray-900 font-medium">{rankings.length}</span> stocks
            </span>
          </div>

          {/* Drop zone for re-upload */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`transition-colors ${dragOver ? 'ring-2 ring-blue-500 rounded-lg' : ''}`}
          >
            {/* Rankings table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <SortHeader field="rank" label="Rank" />
                      <SortHeader field="symbol" label="Symbol" />
                      <SortHeader field="cmaTag" label="CMA Tag" />
                      <SortHeader field="estReturn" label="Est Return" />
                      <SortHeader field="timesRecommended" label="Times Rec" />
                      <SortHeader field="previousRank" label="Prev Rank" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedRankings.map((ranking) => (
                      <tr key={ranking.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2 text-gray-900 font-medium">{ranking.rank}</td>
                        <td className="px-3 py-2 text-blue-600 font-semibold">{ranking.symbol}</td>
                        <td className="px-3 py-2 text-gray-700">{ranking.cmaTag || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">
                          {ranking.estLowReturn != null && ranking.estHighReturn != null
                            ? `${ranking.estLowReturn}–${ranking.estHighReturn}%`
                            : ranking.estLowReturn != null
                            ? `${ranking.estLowReturn}%`
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{ranking.timesRecommended ?? '—'}</td>
                        <td className="px-3 py-2 text-gray-700">
                          {ranking.previousRank != null ? (
                            <span className="flex items-center gap-1">
                              {ranking.previousRank}
                              {ranking.previousRank > ranking.rank && (
                                <span className="text-green-600 text-xs">▲</span>
                              )}
                              {ranking.previousRank < ranking.rank && (
                                <span className="text-red-600 text-xs">▼</span>
                              )}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
